<template>
	<span>
		<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
			<template #activator="{ props }">
				<slot name="button" :props="props">
					<v-icon
						v-if="!hasCustomButton()"
						v-test="'users-filter'"
						class="icon-filter-solid rounded-circle status-label-filter"
						v-bind="props"></v-icon>
				</slot>
			</template>
			<v-card class="search-filter" rounded="lg" elevation="8" @click.stop>
				<v-card-text class="pb-0">
					<v-text-field
						v-model="searchQuery"
						label="Search"
						append-inner-icon="icon-search-magnifying-glass"
						single-line
						variant="outlined"
						v-bind="searchFieldProps(searchQuery)">
					</v-text-field>
				</v-card-text>
				<FilterMenuList
					ref="containerRef"
					:items="sortedUsers"
					:loading="loading"
					:empty-text="searchQuery.trim() ? 'No users match your search' : 'No users available'"
					:is-selected="isSelected"
					loading-test-id="user-filter-loading"
					item-class="w-100 text-truncate user-list-item"
					@toggle="toggleSelected">
					<template #skeleton>
						<v-skeleton-loader type="list-item" class="user-skeleton-row"></v-skeleton-loader>
					</template>
					<template #item="{ item: user }">
						<v-list-item-title>{{ getUserDisplayNameWrapper(user) }}</v-list-item-title>
					</template>
				</FilterMenuList>
				<div class="opacity-50">
					<hr />
				</div>
				<v-card-actions class="d-flex justify-end">
					<v-btn
						color="grey"
						text
						rounded="xl"
						elevation="0"
						class="white-bg"
						@click="clear(() => emit('filter-user', []), true)"
						>Clear</v-btn
					>
					<v-btn
						color="primary"
						rounded="xl"
						elevation="0"
						variant="flat"
						@click="applyFilter(() => emit('filter-user', [...appliedItems]))"
						>Apply</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-menu>
		<span v-if="!hasCustomButton()" v-test="'table-header-users'" class="pl-1">{{ headerText }}</span>

		<v-chip
			v-for="user in appliedItems"
			v-if="!hasCustomButton()"
			:key="user.id"
			:title="getUserDisplayNameWrapper(user)"
			class="px-2 py-1 text-truncate ma-0"
			close
			close-icon="icon-close-small"
			@click:close="removeChip(user)">
			{{ getUserDisplayNameWrapper(user) }}
		</v-chip>
	</span>
</template>

<script setup lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { ref, computed, watch, onMounted, onBeforeUnmount, useSlots } from "vue";
	import { getUserDisplayName } from "../../helpers/utilities";
	import { sortUsersBySelected } from "../../helpers/filters";
	import { useFilterMenu } from "../../composables/useFilterMenu";
	import { useLatestRequest } from "../../composables/useLatestRequest";
	import api from "../../api";
	import { isAbortError } from "../../helpers/abort";
	import { User } from "../../types/User";
	import ResponseArray from "../../types/ResponseArray";
	import FilterMenuList from "./shared/FilterMenuList.vue";
	import { logError } from "../../helpers/logError";

	const props = withDefaults(
		defineProps<{
			headerText?: string;
			preSelectedUsers?: string[];
		}>(),
		{
			headerText: "Filter by user",
			preSelectedUsers: () => [] as string[]
		}
	);

	const emit = defineEmits<{
		(e: "filter-user", users: User[]): void;
	}>();

	// Backend's fastifyPaging middleware caps each request at 1000 rows. Loop in
	// 1000-row chunks up to MAX_USERS so orgs with >1000 users aren't stuck on the
	// first page (LSP-16836 follow-up) — mirrors CustomerFilter.vue's loadCustomers.
	const PAGE_CHUNK = 1000;
	const MAX_USERS = 50000;

	const searchQuery = ref<string>("");
	const users = ref<User[]>([]);
	const loading = ref<boolean>(false);
	const slots = useSlots();
	const requestScope = useLatestRequest();
	const hasCustomButton = (): boolean => slots.button !== undefined;
	let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	const { isFilterOpen, selectedItems, appliedItems, containerRef, isSelected, toggleSelected, applyFilter, clear } = useFilterMenu<User>(
		async () => {
			await loadUsers();
		},
		() => {
			searchQuery.value = "";
		}
	);

	// Computed
	const sortedUsers = computed((): User[] => {
		return sortUsersBySelected(users.value, props.preSelectedUsers);
	});

	// Methods
	const onMount = async (): Promise<void> => {
		await loadUsers(props.preSelectedUsers);
		if (props.preSelectedUsers?.length > 0) {
			selectedItems.value = await setPreselectedUsers(props.preSelectedUsers);
			applyFilter(() => emit("filter-user", appliedItems.value), false);
		} else {
			selectedItems.value = [];
		}
	};

	async function loadUsers(preSelectedUsers: string[] = []): Promise<void> {
		if (exceedsSearchLimit(searchQuery.value)) {
			requestScope.abortCurrent();
			loading.value = false;
			return;
		}
		const requestState = requestScope.startRequest();
		loading.value = true;

		const searchQueryTrimmed = searchQuery.value.trim();
		let where: any = {};

		if (searchQueryTrimmed.length > 0) {
			const parts = searchQueryTrimmed.split(/\s+/).filter((term) => term.length > 0);
			where["$or"] = [];

			if (parts.length > 1) {
				where["$or"] = [
					{
						$and: {
							firstname: { $ilike: `%${parts[0]}%` },
							lastname: { $ilike: `%${parts[1]}%` }
						}
					}
				];
			} else {
				where["$or"].push({
					firstname: { $ilike: `%${searchQueryTrimmed}%` }
				});
				where["$or"].push({
					lastname: { $ilike: `%${searchQueryTrimmed}%` }
				});
			}
		}

		// Trailing `id ASC` tiebreaker: none of the name/phone/email columns are unique, so
		// without it, ties across a page-boundary OFFSET can return the same user on two
		// pages (collected below dedupes defensively) or skip a user entirely.
		const order = [
			["firstname", "ASC NULLS LAST", ["empties.last", "ci"]],
			["lastname", "ASC NULLS LAST", ["empties.last", "ci"]],
			["phone", "ASC NULLS LAST", ["empties.last", "ci"]],
			["email", "ASC NULLS LAST", ["empties.last", "ci"]],
			["id", "ASC"]
		];

		// Keyed by id (not a plain array) so a duplicate row from a page-boundary tie
		// collapses to one entry instead of colliding on FilterMenuList's `:key="item.id"`.
		const collected = new Map<string, User>();
		let page = 1;

		try {
			while (collected.size < MAX_USERS) {
				const result: ResponseArray<User> = await api.getUsers({ where, page, pageSize: PAGE_CHUNK, order }, undefined, {
					signal: requestState.signal
				});

				if (requestScope.isSuperseded(requestState.requestId)) {
					return;
				}

				const sizeBeforePage = collected.size;
				for (const user of result.rows) {
					collected.set(user.id, user);
				}

				// An empty page can never converge (page += 1 forever), regardless of a stale
				// `count` — always stop here.
				if (result.rows.length === 0) break;
				// A non-empty page that added zero NEW ids (100% duplicates of already-collected
				// rows) can't converge either — the dedup Map means `collected.size` no longer
				// grows by `rows.length` every iteration the way a plain array did, so this guard
				// restores the old unconditional-growth termination guarantee: if a backend
				// pagination bug ever returns duplicate-content pages while `count` claims more
				// remain, stop rather than spin forever.
				if (collected.size === sizeBeforePage) {
					logError("UserFilter: loadUsers pagination made no progress on a non-empty page", new Error("no-progress"), {
						page,
						collected: collected.size,
						count: result.count
					});
					break;
				}
				// A short (but non-empty) page is NOT reliably the last page: the flat-query
				// hydration path (apps/contact-api/src/controllers/users.ts) can legitimately
				// return fewer rows than requested for one page while `count` still indicates
				// more remain (e.g. a permission/hydration mismatch on that page only) — trust
				// `count` over the page's row count whenever it's available.
				if (typeof result.count === "number") {
					if (collected.size >= result.count) break;
				} else if (result.rows.length < PAGE_CHUNK) {
					break;
				}
				page += 1;
			}

			users.value = Array.from(collected.values()).slice(0, MAX_USERS);
			await setPreselectedUsers(preSelectedUsers);
		} catch (error) {
			if (isAbortError(error)) {
				return;
			}
			if (!requestScope.isSuperseded(requestState.requestId)) {
				users.value = Array.from(collected.values()).slice(0, MAX_USERS);
				await setPreselectedUsers(preSelectedUsers);
			}
			logError("UserFilter: loadUsers failed", error, { page, collected: collected.size });
		} finally {
			if (!requestScope.isSuperseded(requestState.requestId)) loading.value = false;
		}
	}

	const setPreselectedUsers = async (preSelectedUsers: string[]): Promise<User[]> => {
		try {
			if (preSelectedUsers?.length > 0) {
				const userIds = users.value.map((u) => u.id);
				// only load the selected users if they are not already in the list
				if (preSelectedUsers.some((u) => !userIds.includes(u))) {
					const preSelectedUsersResult: ResponseArray<User> = await api.getUsers({
						where: {
							id: { $in: preSelectedUsers }
						}
					});

					const userMap = new Map<string, User>();
					[...preSelectedUsersResult.rows, ...users.value].forEach((user) => {
						userMap.set(user.id, user);
					});
					users.value = Array.from(userMap.values());
				}
			}
		} catch (error) {
			logError("UserFilter: setPreselectedUsers failed", error, { preSelectedUsers: preSelectedUsers?.length ?? 0 });
			return [];
		}

		// Filters.vue passes `userIds: null` after the filter is cleared, and null bypasses the
		// `= []` default parameter (Vue passes the explicit null, not undefined) — guard it here
		// (LSP-16408, same fix already applied to CustomerFilter.vue's setPreselectedCustomers).
		return (preSelectedUsers ?? []).map((id) => users.value.find((u) => u.id === id)).filter(Boolean) as User[];
	};

	function removeChip(user: User): void {
		appliedItems.value = appliedItems.value.filter((applied) => applied.id !== user.id);
		selectedItems.value = selectedItems.value.filter((selected) => selected.id !== user.id);
		emit("filter-user", [...appliedItems.value]);
	}

	const getUserDisplayNameWrapper = (record: User): string => {
		// auto-elipses via css below
		return getUserDisplayName(record);
	};

	function clearSearchDebounceTimer(): void {
		if (searchDebounceTimer) {
			clearTimeout(searchDebounceTimer);
			searchDebounceTimer = null;
		}
	}

	// Watchers
	watch(searchQuery, () => {
		clearSearchDebounceTimer();
		if (!isFilterOpen.value) return;
		// loadUsers() can now chain up to MAX_USERS pages per call (LSP-16836); debounce
		// keystrokes like CustomerFilter does so typing doesn't fire a multi-page fetch
		// chain per character.
		searchDebounceTimer = setTimeout(() => {
			// Re-check at fire time, not just schedule time: closeMenu()'s searchQuery reset
			// is a no-op (no watcher fire) when the field was already empty, so a pending
			// timer scheduled while the menu was open can otherwise still fire after close.
			if (!isFilterOpen.value) return;
			loadUsers();
		}, 300);
	});

	watch(
		() => props.preSelectedUsers,
		async (newVal: string[], oldVal: string[] | undefined) => {
			if (!newVal || newVal.length == 0) {
				return;
			}

			if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
				selectedItems.value = await setPreselectedUsers(newVal);
				applyFilter(() => emit("filter-user", appliedItems.value), false);
			}
		}
	);

	// Lifecycle
	onMounted(async () => {
		await onMount();
	});

	onBeforeUnmount(() => {
		clearSearchDebounceTimer();
	});

	async function restore(): Promise<void> {
		if (props.preSelectedUsers?.length > 0) {
			await setPreselectedUsers(props.preSelectedUsers);
			selectedItems.value = props.preSelectedUsers.map((u) => users.value.find((x) => x.id === u)).filter(Boolean) as User[];
			applyFilter(() => emit("filter-user", appliedItems.value), false);
		}
	}

	defineExpose({
		clear: (emitEvent: boolean = false) => clear(() => emit("filter-user", []), emitEvent),
		restore,
		loadUsers,
		isFilterOpen,
		loading
	});
</script>

<style scoped>
	.user-skeleton-row {
		background: transparent;
	}

	.status-label-filter {
		width: 28px;
		height: 28px;
		font-size: 1rem;
		background-color: #b1d8ff;
		color: #0070ff;
	}

	.status-label-chip {
		font-size: 12px;
		height: 28px;
		box-sizing: border-box;
	}
</style>
